import React, { useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { ArrowElem, DeltaNumber } from 'components/Gadget/Delta';
import {
  COL_BORDER_COLOR,
  GADGET_HEADERS_BACKGROUND_COLOR,
  HEADERS_BORDER_COLOR,
  ODD_ROW_COLOR,
  ROW_HEIGHTS,
} from 'components/Grid/StyleOverrides';
import Typography from 'kingpin/atoms/Typography';

import { VisTypeIcon } from '../visTypes';
import { isReducedMotion } from './constants';
import SampleChart from './SampleChart';

// The previews of the gadget types that aren't charts, drawn as their
// gadgets draw: a Number's value and change, a trend card's periods, a
// Table's grid. Made-up fleet figures, as the charts' samples.

const COUNT_UP_MS = 900;
const STAGGER_MS = 70;

// A figure counting up from zero as the preview opens
const useCountUp = (target: number): number => {
  const [value, setValue] = useState<number>(() =>
    isReducedMotion() ? target : 0,
  );
  useEffect(() => {
    if (isReducedMotion() || typeof requestAnimationFrame !== 'function') {
      setValue(target);
      return undefined;
    }
    let frame: number;
    const start = performance.now();
    const step = (now: number) => {
      const progress = Math.min(1, (now - start) / COUNT_UP_MS);
      // Ease out: quick at first, settling onto the figure
      setValue(target * (1 - Math.pow(1 - progress, 3)));
      if (progress < 1) {
        frame = requestAnimationFrame(step);
      }
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [target]);
  return value;
};

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Each of a preview's items rising in a beat after the one before
const staggered = css<{ $index: number }>`
  animation: ${riseIn} 0.4s ease-out both;
  animation-delay: ${(props) => props.$index * STAGGER_MS}ms;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 100%;
  text-align: center;
`;

// As a Number block's value (see StudioCard)
const Value = styled.div<{ $fontSize: number }>`
  font-size: ${(props) => props.$fontSize}px;
  font-weight: 600;
  line-height: 1.1;
  color: var(--color-text-primary);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const Rise = styled.div<{ $index: number }>`
  ${staggered}
`;

// As ComparisonBadge draws a % change
const Change = ({ percent, isGood }: { percent: number; isGood: boolean }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <ArrowElem isGood={isGood} delta={percent} isGrid={false} />
    <DeltaNumber isGood={isGood} isGrid={false}>
      {`${Math.abs(percent).toFixed(1)}%`}
    </DeltaNumber>
  </div>
);

const Caption = ({ children }: { children: string }) => (
  <Typography.Body type="Body 12" color="var(--color-text-tertiary)">
    {children}
  </Typography.Body>
);

const toMoney = (value: number) => `$${Math.round(value).toLocaleString()}`;

const SampleNumber = () => {
  const value = useCountUp(248600);
  return (
    <Centered>
      <Value $fontSize={28}>{toMoney(value)}</Value>
      <Rise $index={4}>
        <Change percent={8.2} isGood />
      </Rise>
      <Rise $index={5}>
        <Caption>vs last month</Caption>
      </Rise>
    </Centered>
  );
};

const TrendPeriods = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  height: 100%;
  align-items: center;
`;

const TrendPeriodValue = ({ value }: { value: number }) => {
  const counted = useCountUp(value);
  return <Value $fontSize={20}>{Math.round(counted).toLocaleString()}</Value>;
};

// As TrendCard: each period's label, dates, value and change on the one
// before
const SampleTrend = () => (
  <TrendPeriods>
    {[
      { label: 'Latest', dates: '9/14 – 10/13', value: 1284, change: 6.4 },
      {
        label: 'Previous',
        dates: '8/15 – 9/13',
        value: 1207,
        change: -2.1,
      },
      { label: 'Before', dates: '7/16 – 8/14', value: 1233, change: 3.8 },
    ].map((period, index) => (
      <Rise key={period.label} $index={index}>
        <Centered>
          <Typography.Body type="Label">
            {period.label.toUpperCase()}
          </Typography.Body>
          <Typography.Body type="Body 12" color="var(--color-grey-5)">
            {period.dates}
          </Typography.Body>
          <TrendPeriodValue value={period.value} />
          <Change percent={period.change} isGood={period.change >= 0} />
        </Centered>
      </Rise>
    ))}
  </TrendPeriods>
);

// As a Table's grid (see Grid/StyleOverrides): a shaded header, striped rows
// and a bold total
const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto auto;
  border: 1px solid ${HEADERS_BORDER_COLOR};
  border-radius: 4px;
  overflow: hidden;
  font-size: 11px;
  color: var(--color-text-primary);
  font-variant-numeric: tabular-nums;
`;

const GridRow = styled.div<{ $index: number; $isHeader?: boolean }>`
  display: contents;

  > div {
    ${staggered}
    display: flex;
    align-items: center;
    height: ${ROW_HEIGHTS.XXS}px;
    padding: 0 8px;
    border-bottom: 1px solid ${COL_BORDER_COLOR};
    white-space: nowrap;
    overflow: hidden;
    ${(props) =>
      props.$isHeader &&
      css`
        background-color: ${GADGET_HEADERS_BACKGROUND_COLOR};
        font-weight: 550;
      `}
    ${(props) =>
      !props.$isHeader &&
      props.$index % 2 === 0 &&
      css`
        background-color: ${ODD_ROW_COLOR};
      `}
  }

  > div:not(:first-child) {
    justify-content: flex-end;
  }

  &:last-child > div {
    border-bottom: none;
    font-weight: 600;
  }
`;

const TABLE_ROWS = [
  ['Region', 'Loads', 'Revenue', 'On time'],
  ['North', '312', '$84,210', '96%'],
  ['South', '468', '$121,960', '92%'],
  ['West', '391', '$102,550', '89%'],
  ['Total', '1,171', '$308,720', '92%'],
];

const SampleTable = () => (
  <Grid>
    {TABLE_ROWS.map((row, index) => (
      <GridRow key={row[0]} $index={index} $isHeader={index === 0}>
        {row.map((cell) => (
          <div key={cell}>{cell}</div>
        ))}
      </GridRow>
    ))}
  </Grid>
);

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  height: 100%;
  justify-content: center;
  padding: 0 4px;
`;

const SampleText = () => (
  <TextBlock>
    <Rise $index={0}>
      <Typography.Header type="H5">Weekly operations review</Typography.Header>
    </Rise>
    <Rise $index={1}>
      <Typography.Body type="Body 12" color="var(--color-text-secondary)">
        Loads are up <b>8%</b> on last week, led by the South. Reefer capacity
        is tight heading into the holidays.
      </Typography.Body>
    </Rise>
    <Rise $index={2}>
      <Typography.Body type="Body 12" color="var(--color-text-secondary)">
        • Review West on-time performance
      </Typography.Body>
    </Rise>
  </TextBlock>
);

const CompositeLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 4px;
`;

const CompositeNumbers = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  flex-shrink: 0;
`;

const CompositeNumber = ({
  label,
  value,
  format,
  percent,
}: {
  label: string;
  value: number;
  format: (value: number) => string;
  percent: number;
}) => {
  const counted = useCountUp(value);
  return (
    <Centered>
      <Caption>{label}</Caption>
      <Value $fontSize={18}>{format(counted)}</Value>
      <Change percent={percent} isGood={percent >= 0} />
    </Centered>
  );
};

const COMPOSITE_CHART_HEIGHT = 56;

const CompositeChart = styled.div`
  height: ${COMPOSITE_CHART_HEIGHT}px;
  margin-top: auto;
`;

// Numbers above a chart of the same metrics, in one gadget
const SampleComposite = () => (
  <CompositeLayout>
    <CompositeNumbers>
      <CompositeNumber
        label="Loads"
        value={590}
        format={(v) => `${Math.round(v)}`}
        percent={9.7}
      />
      <CompositeNumber
        label="Last year"
        value={488}
        format={(v) => `${Math.round(v)}`}
        percent={4.8}
      />
    </CompositeNumbers>
    <CompositeChart>
      <SampleChart
        visType="line"
        isLegendHidden
        height={COMPOSITE_CHART_HEIGHT}
      />
    </CompositeChart>
  </CompositeLayout>
);

const MediaFrame = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 100%;
  border-radius: 6px;
  background-color: var(--color-bg-secondary);
  color: var(--color-icon-tertiary);

  svg {
    width: 40px;
    height: 40px;
  }
`;

// A picture, video or web page: its kind's icon in a frame, as there's no
// made-up file to show
const SampleMedia = ({
  visType,
  caption,
}: {
  visType: Studio.MediaVisType;
  caption: string;
}) => (
  <MediaFrame>
    <Rise $index={0}>
      <VisTypeIcon visType={visType} />
    </Rise>
    <Rise $index={1}>
      <Caption>{caption}</Caption>
    </Rise>
  </MediaFrame>
);

export const SAMPLE_CARDS: Partial<Record<Studio.VisType, () => JSX.Element>> =
  {
    number: SampleNumber,
    trend: SampleTrend,
    table: SampleTable,
    text: SampleText,
    composite: SampleComposite,
    image: () => <SampleMedia visType="image" caption="company-logo.png" />,
    video: () => <SampleMedia visType="video" caption="safety-briefing.mp4" />,
    iframe: () => (
      <SampleMedia visType="iframe" caption="https://example.com" />
    ),
  };
